import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Color, LoadingGroup, Pager, Spinner, Table, useSimpleTableDataSource } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";

import { UniformRowColorTable } from "@/App/components";

import { useGetEligibilityHourBankDatasourceQuery } from "../../api";
import { InformationPanelProps } from "../../components";
import { DisplayField } from "../../components/DisplayField";
import { HoursHistoryBaseType } from "../../interface";

export const HoursBank: FC<InformationPanelProps> = ({ eligibilityMemberData, isLoading }) => {
  const { t: translate } = useTranslation();

  const { data: hoursBankListResults, isLoading: hoursBankListResultsLoading } = useGetEligibilityHourBankDatasourceQuery();

  const hoursBankRows = useMemo(() => {
    return (hoursBankListResults?.results || []).map((hoursBank: HoursHistoryBaseType) => {
      return {
        id: hoursBank.id,
        data: {
          ...hoursBank,
        },
      };
    });
  }, [hoursBankListResults]);

  const hoursHistoryDataSource = useSimpleTableDataSource(hoursBankRows, { pageSize: 10 });

  return (
    <LoadingGroup loading={hoursBankListResultsLoading || isLoading}>
      <LoadingGroup.Placeholder
        fallback={
          <Shelf>
            <Spinner size="large" />
          </Shelf>
        }
      >
        <Stack>
          <Stack spacing="large">
            <Subheading>{translate("ELIGIBILITY.hoursBank")}</Subheading>
            <Grid>
              <Grid.Span columns={2}>
                <DisplayField title={translate("ELIGIBILITY.totalHoursBanked")} isTitleSemibold={false}>
                  <Semibold>{eligibilityMemberData?.hours_data?.total_hours_paid ?? "--"}</Semibold>
                </DisplayField>
              </Grid.Span>
            </Grid>

            <UniformRowColorTable
              dataSource={hoursHistoryDataSource}
              footer={<Pager dataSource={hoursHistoryDataSource} testId="hours_bank_table_pagination" />}
            >
              <Table.Date
                name="date_of_change"
                title={translate("ELIGIBILITY.dateOfChange")}
                sortable="date_of_change"
                initialWidth="span"
                format="MM/dd/yyyy"
              />
              <Table.Custom
                name="hours_paid"
                title={translate("ELIGIBILITY.hoursAdjustment")}
                sortable="hours_paid"
                initialWidth="span"
                align="right"
              >
                {(_, column_id) => {
                  const { hours_paid } = hoursHistoryDataSource.lookup(column_id);
                  const red = Color.toString(Color.Red["600"]);
                  const green = Color.toString(Color.Green["700"]);
                  return parseFloat(hours_paid) < 0 ? (
                    <span style={{ color: red }}>{hours_paid}</span>
                  ) : (
                    <span style={{ color: green }}>+{hours_paid}</span>
                  );
                }}
              </Table.Custom>
              <Table.Text name="hours_type" title={translate("ELIGIBILITY.type")} sortable="hours_type" initialWidth="span" />
              <Table.Text
                name="sum_hours"
                title={translate("ELIGIBILITY.hoursBank")}
                sortable="sum_hours"
                initialWidth="span"
              />
            </UniformRowColorTable>
          </Stack>
        </Stack>
      </LoadingGroup.Placeholder>
    </LoadingGroup>
  );
};
